import { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getChartTheme, DENOMINATION_COLORS, formatCompactCurrency, formatFullCurrency, getTooltipStyle, getChartHeight } from './chartTheme';

const CustomTooltip = ({ active, payload, label }) => {
  const theme = getChartTheme();
  if (!active || !payload?.length) return null;

  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
  const total = sorted.reduce((sum, e) => sum + (e.value || 0), 0);

  return (
    <div style={getTooltipStyle(theme)}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {sorted.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: entry.color, flexShrink: 0 }} />
          <span style={{ color: theme.textColor }}>{entry.dataKey}:</span>
          <span style={{ fontWeight: 600 }}>{formatFullCurrency(entry.value)}</span>
        </div>
      ))}
      <div style={{ borderTop: `1px solid ${theme.gridColor}`, marginTop: 4, paddingTop: 4, fontWeight: 600 }}>
        Total: {formatFullCurrency(total)}
      </div>
    </div>
  );
};

const CustomLegend = ({ payload }) => {
  const theme = getChartTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px 20px', paddingTop: 8 }}>
      {payload?.map((entry) => (
        <div key={entry.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.labelColor }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: entry.color, flexShrink: 0 }} />
          {entry.value}
        </div>
      ))}
    </div>
  );
};

/**
 * Liability by Denomination
 * Stacked area chart showing dollar value of bonds written per denomination per month
 *
 * @param {{ data: Array, denominationKeys: string[] }} props
 *   data: [{ month: 'APR', '$5,500': 71500, '$11,000': 132000, ... }]  (liability dollars)
 *   denominationKeys: ['$5,500', '$11,000', ...]
 */
const PowerAmountChart = ({ data, denominationKeys }) => {
  const theme = getChartTheme();
  const chartHeight = useMemo(() => getChartHeight(), []);

  if (!data?.length || !denominationKeys?.length) return null;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />
        <XAxis
          dataKey="month"
          tick={{ fill: theme.textColor, fontSize: 12 }}
          axisLine={{ stroke: theme.gridColor }}
          tickLine={{ stroke: theme.gridColor }}
        />
        <YAxis
          tick={{ fill: theme.textColor, fontSize: 12 }}
          axisLine={{ stroke: theme.gridColor }}
          tickLine={{ stroke: theme.gridColor }}
          tickFormatter={formatCompactCurrency}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={<CustomLegend />} />
        {denominationKeys.map((key, idx) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            name={key}
            stackId="1"
            stroke={DENOMINATION_COLORS[idx % DENOMINATION_COLORS.length]}
            fill={DENOMINATION_COLORS[idx % DENOMINATION_COLORS.length]}
            fillOpacity={0.6}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default PowerAmountChart;
